/**
 * #2036 / #2034 — the ONE allow-list for stored rich text, and the only safe way to render it.
 *
 * ══ WHY THIS EXISTS ══
 *
 * `clients.login_page_message` is written by a Client Admin (or a Power User the CA nominated as
 * a splash editor, #1741) and rendered with `v-html` on the tenant's **login page**, which is
 * served to anyone who opens it — before authentication, on the page where people type their
 * password. `GET /api/v2/auth/domain` is registered outside `auth:api` and returns the whole
 * `clients` row, so the stored string reaches the browser of every visitor. Until #2036 the only
 * transformation applied was `replace(/<a /g, '<a target="_blank" rel="noopener" ')` — which
 * ADDS an attribute and removes nothing.
 *
 * ══ THE SERVER TWIN IS NOT OPTIONAL ══
 *
 * `app/Support/SafeHtml.php` carries the SAME three lists. `tests/Unit/SafeHtmlTest.php` reads
 * THIS FILE and asserts they are equal, because a browser rule and a server rule that disagree
 * is the whole failure mode #2034 exists to close — and #1952's lesson was that one shared rule
 * set beats ten copies that drift.
 *
 *   server (SafeHtml::validate)   REJECTS on save, naming the field and the offender. Never
 *                                 rewrites what the user typed — ed's #1952 rule.
 *   browser (sanitize, here)      STRIPS on display and on paste, because every row written
 *                                 before #2036 is unvalidated and must still be harmless.
 *
 * ══ WHY DOMPurify 2.x AND NOT 3.x ══
 *
 * `babel-loader` includes only `src`, `test` and `webpack-dev-server/client`, so a dependency
 * ships into the bundle UNTRANSPILED; and `.babelrc` targets `> 1%, last 2 versions, not ie <= 8`,
 * which still includes IE 10/11. DOMPurify 3.x's dist is ES2015 (215 `const`, 80 `let`, 11
 * arrows); 2.x's is ES5. Terser would minify either, so this is about the browser target, not
 * the build. 2.5.9 is the latest 2.x and the line still gets security fixes. Revisit with a
 * browserslist modernisation, not here.
 *
 * ══ DOMPurify IS A FACTORY UNTIL IT HAS A WINDOW — AND THAT IS WHY EVERY CALL IS GUARDED ══
 *
 * `require('dompurify')` with no global `window` at import time returns the uninitialised
 * factory: `isSupported` is false and it returns BEFORE `sanitize`/`addHook` are assigned, so
 * `DOMPurify.sanitize` is literally `undefined`. Measured in plain node — `typeof export` is
 * `function`, `typeof sanitize` is `undefined`.
 *
 * In the bundle that never happens, because `window.document` exists when the module evaluates.
 * But "never happens in the browser" is not a reason to call an undefined function, so every
 * entry point below checks `typeof DOMPurify.sanitize === 'function'` and falls back to
 * `escapeHtml`. **The fallback escapes rather than passes through** — a function called
 * `sanitize` returning its input is the worst failure available here, so the degraded mode
 * loses formatting instead of losing safety. `safe-html-rules-2036.test.mjs` asserts exactly
 * that, which is also what makes a DOM-less unit test meaningful.
 *
 * ══ THE EDITOR IS NOT A SECURITY BOUNDARY ══
 *
 * The toolbar is built from `ALLOWED_TAGS` so it cannot offer what the rules refuse, and the
 * vendored CKEditor is 4.12.1 (2019) with later CVEs in its line. Treat it as UX. The boundaries
 * are the server validator and `sanitize()` here.
 */
import DOMPurify from 'dompurify';

/**
 * ══ THE ALLOW-LIST ══
 *
 * ed, 2026-10-03: bold, italic, underline, paragraphs, line breaks, bullet/numbered lists,
 * links. Taken literally — no headings, colour, font, tables or images. A splash page using
 * one of those today will be refused on its next save, which is the intended behaviour.
 *
 * ⚠ Keep these three in step with `app/Support/SafeHtml.php`, in the same order. A test
 * compares them; do not "fix" a difference on one side alone.
 */
export const ALLOWED_TAGS = ['p', 'br', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'a', 'span'];

/**
 * Attributes, per tag. Nothing is allowed anywhere else — no `class`, no `id`, no `data-*`.
 *
 * `span` + `style` exist because of what the toolbar actually emits. Measured in the vendored
 * `ckeditor.js`, not assumed:
 *
 *     colorButton_foreStyle  ->  <span style="color:#RRGGBB">
 *     colorButton_backStyle  ->  <span style="background-color:…">
 *     font (FontSize)        ->  <span style="font-size:24px">
 *     justify                ->  style="text-align:center" on the block (p / li / ul / ol)
 *
 * `title` on `a` carries the link's real destination (ed, 2026-10-04) so a reader can see where
 * it goes before clicking.
 */
export const ALLOWED_ATTR = {
  a: ['href', 'target', 'rel', 'title'],
  p: ['style'],
  span: ['style'],
  li: ['style'],
  ul: ['style'],
  ol: ['style'],
};

/**
 * ══ PROFILES — #2041 ══
 *
 * ⚠ WHY THIS IS NOT ONE LIST ANY MORE.
 *
 * ed accepted #2036 on the stated understanding that the login message has NO headings — the
 * READY note said a splash page using one "will be refused on its next save", and he took it.
 * #2041 needs headings for a privacy policy. Adding `h2` to a single shared list would have
 * quietly changed the rules on the login page he had already signed off.
 *
 * So the module grows one DIMENSION rather than one entry. Everything else — the style
 * properties and their values, the schemes, the link red flags, the shortener lists — is shared,
 * because none of it has any reason to differ by field.
 *
 *   loginMessage   #2036's list exactly. The default, so every #2036 call site is unchanged.
 *   policy         + h2 h3 h4, and a much longer length limit.
 *
 * h2-h4 and not h1: `privacyPolicyReview.vue:15` already renders the policy TITLE as an `<h3>`,
 * so an `h1` inside the body competes with the page's own heading structure.
 *
 * ⚠ There is no heading BUTTON. The vendored CKEditor build has 18 plugins and `format` — the
 * heading dropdown — is not one of them (nor `stylescombo`, `pastefromword`, `pastetext`). So
 * headings are supported in the MARKUP, which is what makes a pasted or legacy policy render as
 * written; putting a button on the toolbar needs that plugin vendored into the repo, which is
 * ed's call (#2041 plan, §2).
 */
export const POLICY_EXTRA_TAGS = ['h2', 'h3', 'h4'];

/** The policy profile's tags: the base list plus headings. */
export const POLICY_TAGS = ALLOWED_TAGS.concat(POLICY_EXTRA_TAGS);

/** Headings carry `style` too — alignment and colour on a heading are ordinary policy text. */
export const POLICY_ATTR = Object.assign({}, ALLOWED_ATTR, {
  h2: ['style'], h3: ['style'], h4: ['style'],
});

/**
 * A policy is a document, not a greeting. `privacy_policies.content` is `longText`, so 100,000
 * is still far under the column while being a real ceiling on a paste.
 */
export const POLICY_MAX_LENGTH = 100000;

/** `http:` is deliberately NOT here. ed: "b - a" = https only. */
export const ALLOWED_SCHEMES = ['https', 'mailto'];

/** What every surviving link is forced to carry. */
export const LINK_ATTRS = { target: '_blank', rel: 'noopener noreferrer' };

/**
 * ══ THE STYLE ALLOW-LIST ══
 *
 * ⚠ DOMPurify 2.x DOES NOT SANITIZE CSS. Verified: zero occurrences of `expression` or `url(`
 * in its dist. Putting `style` in `ALLOWED_ATTR` keeps the declaration block VERBATIM — so the
 * filtering below is not belt-and-braces, it is the only thing standing between a `style`
 * attribute and `background:url(javascript:…)` / `position:fixed` overlaying the login form.
 *
 * Properties are allow-listed AND their values are pattern-checked. A property whose value does
 * not match is dropped, not "cleaned" — ed's #1952 rule applies to CSS too.
 *
 * ed, 2026-10-04 09:27 — option **(b)**: colour, highlight, preset size, alignment **and font
 * family from a preset list**. *"`font-family` is accepted only when it exactly matches a
 * preset; anything else is refused with a message on the server and dropped in the editor and
 * display, like the other style properties."*
 */
export const STYLE_FONT_FAMILY_ENABLED = true;

/**
 * The font stacks the toolbar offers, and the ONLY `font-family` values accepted.
 *
 * ed's list — Arial/Helvetica, Georgia, Times New Roman, Verdana, Courier New — plus the app's
 * own font, which is `'Roboto', 'Helvetica'` in the web app's stylesheets.
 *
 * `ckEditorFontNames()` builds the editor's `font_names` from THIS array, so what the toolbar
 * can emit and what the validator accepts cannot drift. Comparison is on the NORMALISED form
 * (see normaliseFontFamily) because CKEditor quotes multi-word names and the stored bytes
 * therefore differ from the literals here.
 */
export const FONT_FAMILIES = [
  'Roboto, Helvetica, Arial, sans-serif',
  'Arial, Helvetica, sans-serif',
  'Georgia, serif',
  'Times New Roman, Times, serif',
  'Verdana, Geneva, sans-serif',
  'Courier New, Courier, monospace',
];

/**
 * One spelling of a font stack, so `'Times New Roman', Times, serif` and
 * `Times New Roman,Times,serif` compare equal: lower-cased, unquoted, single-spaced after commas.
 */
export function normaliseFontFamily(value) {
  return String(value == null ? '' : value)
    .toLowerCase()
    .replace(/["']/g, '')
    .split(',')
    .map((part) => part.trim().replace(/\s+/g, ' '))
    .filter((part) => part !== '')
    .join(', ');
}

const FONT_FAMILY_SET = new Set(FONT_FAMILIES.map(normaliseFontFamily));

/** The preset sizes the toolbar offers. The validator accepts EXACTLY these — nothing else. */
export const FONT_SIZES = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '36px', '48px'];

/** `text-align` values, matching what `justify` emits. */
export const TEXT_ALIGNS = ['left', 'center', 'right', 'justify'];

/** The 148 CSS named colours, plus the two keywords. Kept as a Set for the value check. */
const NAMED_COLOURS = new Set(('aliceblue antiquewhite aqua aquamarine azure beige bisque black blanchedalmond blue '
  + 'blueviolet brown burlywood cadetblue chartreuse chocolate coral cornflowerblue cornsilk crimson cyan darkblue '
  + 'darkcyan darkgoldenrod darkgray darkgrey darkgreen darkkhaki darkmagenta darkolivegreen darkorange darkorchid '
  + 'darkred darksalmon darkseagreen darkslateblue darkslategray darkslategrey darkturquoise darkviolet deeppink '
  + 'deepskyblue dimgray dimgrey dodgerblue firebrick floralwhite forestgreen fuchsia gainsboro ghostwhite gold '
  + 'goldenrod gray grey green greenyellow honeydew hotpink indianred indigo ivory khaki lavender lavenderblush '
  + 'lawngreen lemonchiffon lightblue lightcoral lightcyan lightgoldenrodyellow lightgray lightgrey lightgreen '
  + 'lightpink lightsalmon lightseagreen lightskyblue lightslategray lightslategrey lightsteelblue lightyellow lime '
  + 'limegreen linen magenta maroon mediumaquamarine mediumblue mediumorchid mediumpurple mediumseagreen '
  + 'mediumslateblue mediumspringgreen mediumturquoise mediumvioletred midnightblue mintcream mistyrose moccasin '
  + 'navajowhite navy oldlace olive olivedrab orange orangered orchid palegoldenrod palegreen paleturquoise '
  + 'palevioletred papayawhip peachpuff peru pink plum powderblue purple rebeccapurple red rosybrown royalblue '
  + 'saddlebrown salmon sandybrown seagreen seashell sienna silver skyblue slateblue slategray slategrey snow '
  + 'springgreen steelblue tan teal thistle tomato transparent turquoise violet wheat white whitesmoke yellow '
  + 'yellowgreen inherit').split(' '));

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_HSL = /^(?:rgb|rgba|hsl|hsla)\(\s*[0-9a-z%.,\s/+-]+\)$/i;

/** True when `v` is a colour and only a colour — no `url()`, no function smuggling. */
function isColourValue(v) {
  const s = String(v).trim().toLowerCase();
  if (s === '') return false;
  if (HEX.test(s)) return true;
  if (NAMED_COLOURS.has(s)) return true;
  // rgb()/hsl() are allowed, but the argument list is restricted to digits, %, commas,
  // whitespace and sign — so `rgb(var(--x))` and `rgb(1,2,3);background:url(…)` cannot pass.
  return RGB_HSL.test(s);
}

/**
 * A font stack, and ONLY an exact preset.
 *
 * ed: "accepted only when it exactly matches a preset". A pattern check (`/^[a-z0-9 ,'"-]+$/`)
 * would accept `font-family: SomeoneElsesFont` and every other stack a paste could carry, which
 * is not what was asked for — and `font-family` is the one property whose value is attacker-
 * chosen text rather than a colour or a keyword, so the set has to be closed.
 */
function isFontFamilyValue(v) {
  return STYLE_FONT_FAMILY_ENABLED && FONT_FAMILY_SET.has(normaliseFontFamily(v));
}

/**
 * The properties, and the rule each value must satisfy.
 *
 * ⚠ Keep in step with `SafeHtml::STYLE_PROPERTIES` and its value checks — the agreement test
 * compares the property names, the preset sizes and the align values.
 */
export const STYLE_PROPERTIES = {
  'color': isColourValue,
  'background-color': isColourValue,
  'font-size': (v) => FONT_SIZES.indexOf(String(v).trim().toLowerCase()) !== -1,
  'text-align': (v) => TEXT_ALIGNS.indexOf(String(v).trim().toLowerCase()) !== -1,
  'font-family': isFontFamilyValue,
};

/**
 * Patterns refused categorically, wherever they appear in a declaration block — before any
 * per-property check, because these are the shapes that make CSS a script sink or a
 * clickjacking tool rather than formatting.
 */
export const STYLE_FORBIDDEN = [
  { pattern: /url\s*\(/i, label: 'url() in a style' },
  { pattern: /expression\s*\(/i, label: 'expression() in a style' },
  { pattern: /\\/, label: 'a backslash escape in a style' },
  { pattern: /\/\*/, label: 'a comment in a style' },
  { pattern: /@/, label: 'an at-rule in a style' },
  { pattern: /&#|&\w+;/, label: 'an HTML entity in a style' },
  { pattern: /!\s*important/i, label: '!important in a style' },
  { pattern: /position\s*:/i, label: 'position in a style' },
];

/**
 * ══ LINK RULES BEYOND THE SCHEME (ed, 2026-10-04) ══
 *
 * Our own shortener is accepted everywhere; third-party ones are not, because their destination
 * cannot be judged at review time.
 *
 * ⚠ EXACT HOST, never a substring. `cs3.me.evil.com` and `notcs3.me` must NOT inherit the
 * exemption — that is the standard way an allow-list of this shape leaks.
 */
export const OWN_SHORTENER_HOSTS = ['cs3.me', 'www.cs3.me'];

/** Extends #1952's `bit.ly|tinyurl|goo.gl` rather than starting a second copy of that list. */
export const THIRD_PARTY_SHORTENERS = [
  'bit.ly', 'tinyurl.com', 'goo.gl', 't.co', 'ow.ly', 'is.gd', 'buff.ly',
  'rebrand.ly', 'cutt.ly', 'shorturl.at', 'rb.gy', 'tiny.cc', 'bitly.com', 't.ly',
];

/** The app-level ceiling. The column type is a `TEXT` of unverified width, so sit well under it. */
export const MAX_LENGTH = 10000;

/**
 * The registry. `profileOf()` is deliberately forgiving about an unknown name — it falls back to
 * the NARROWEST profile, so a typo tightens the rules rather than loosening them.
 *
 * ⚠ Keep in step with `SafeHtml::PROFILES`; the agreement test compares them per profile.
 */
export const PROFILES = {
  loginMessage: { tags: ALLOWED_TAGS, attr: ALLOWED_ATTR, maxLength: MAX_LENGTH },
  policy: { tags: POLICY_TAGS, attr: POLICY_ATTR, maxLength: POLICY_MAX_LENGTH },
};

export const DEFAULT_PROFILE = 'loginMessage';

export function profileOf(name) {
  return PROFILES[name] || PROFILES[DEFAULT_PROFILE];
}

/**
 * Flat `ALLOWED_ATTR` for DOMPurify, which takes one list rather than a per-tag map.
 *
 * ⚠ So DOMPurify alone would allow `style` on an `<a>` and `href` on a `<span>`. The PER-TAG
 * map is re-imposed by the `afterSanitizeAttributes` hook below, and the server validates
 * per-tag too. Flattening is DOMPurify's interface, not the rule.
 */
function flatAttr(attrMap) {
  return Object.keys(attrMap).reduce(
    (acc, tag) => acc.concat(attrMap[tag].filter((a) => acc.indexOf(a) === -1)),
    [],
  );
}

/**
 * `https:` or `mailto:` only — and that judgement has to be made on the RESOLVED url, not on
 * the string. A browser resolves `//evil.test/x` against the page's protocol and
 * `\/\/evil.test` too, so a `startsWith('https://')` test is not the same question.
 */
export function isAllowedHref(href) {
  const raw = String(href == null ? '' : href).trim();
  if (raw === '') return false;
  // A relative or protocol-relative url has no scheme of its own and must not be guessed at.
  if (/^[\\/]{2}/.test(raw)) return false;
  const m = /^([a-z][a-z0-9+.-]*):/i.exec(raw);
  if (!m) return false;

  return ALLOWED_SCHEMES.indexOf(m[1].toLowerCase()) !== -1;
}

/**
 * Nodes DOMPurify journals that are NOT the user's content.
 *
 * It parses into a document, so `<body>` is "removed" on EVERY call. Reporting that to someone
 * who pasted a paragraph would read "Removed <body>", which is both alarming and false.
 */
const STRUCTURAL = ['body', 'html', 'head', '#document', '#document-fragment'];

/** `https` for an https url, `mailto` for a mailto, else the scheme as written, else ''. */
export function schemeOf(href) {
  const m = /^([a-z][a-z0-9+.-]*):/i.exec(String(href == null ? '' : href).trim());

  return m ? m[1].toLowerCase() : '';
}

/**
 * The URL's host, lower-cased, or '' when it has none.
 *
 * Uses the browser's own parser where there is one, so the answer is the host the browser WILL
 * connect to rather than the one a regex guesses at. Falls back to a regex with no DOM, which
 * is only a unit-test path.
 */
export function hostOf(href) {
  const raw = String(href == null ? '' : href).trim();
  if (raw === '') return '';
  if (typeof URL === 'function') {
    try {
      return new URL(raw).hostname.toLowerCase();
    } catch (e) {
      return '';
    }
  }
  const m = /^[a-z][a-z0-9+.-]*:\/\/(?:[^/@]*@)?([^/:?#]+)/i.exec(raw);

  return m ? m[1].toLowerCase() : '';
}

/** True when the url carries `user:pass@` — the visible text then lies about the host. */
export function hasCredentials(href) {
  const raw = String(href == null ? '' : href).trim();
  if (typeof URL === 'function') {
    try {
      const u = new URL(raw);

      return u.username !== '' || u.password !== '';
    } catch (e) { /* fall through to the regex */ }
  }

  return /^[a-z][a-z0-9+.-]*:\/\/[^/?#]*@/i.test(raw);
}

/**
 * True when the host is a literal address rather than a name.
 *
 * Covers dotted IPv4, bracketed IPv6, AND the all-digit / hex forms a browser still resolves
 * (`https://3232235777/` is 192.168.1.1, `https://0x7f000001/` is 127.0.0.1). Those are the
 * evasion, not the obvious case.
 */
export function isIpHost(host) {
  const h = String(host == null ? '' : host).trim().toLowerCase().replace(/^\[|\]$/g, '');
  if (h === '') return false;
  if (h.indexOf(':') !== -1) return true;                            // IPv6, bracketed or bare

  // EVERY dot-separated label numeric in SOME base, 1-4 labels. That is what a browser will
  // resolve, and it is the only formulation that catches all the forms at once:
  //
  //     127.0.0.1      dotted decimal
  //     3232235777     integer          (192.168.1.1)
  //     0x7f000001     hex              (127.0.0.1)
  //     0177.0.0.1     dotted OCTAL     (127.0.0.1)  <- found by this file's own test
  //     0x7f.1         mixed
  //
  // A per-form list missed the octal-dotted case, because `0177` is four digits and the dotted
  // quad pattern allows three. Asking "is every label a number" has no such seam — and it does
  // not catch a real hostname, because `abc.def`'s labels are not numbers in any base (hex
  // needs the `0x`).
  const labels = h.split('.');
  if (labels.length > 4) return false;

  return labels.every((label) => (
    /^\d+$/.test(label) || /^0x[0-9a-f]+$/.test(label) || /^0[0-7]+$/.test(label)
  ));
}

/** Exact host match against a list — never a substring. See OWN_SHORTENER_HOSTS. */
function hostIn(host, list) {
  const h = String(host || '').toLowerCase();

  return list.some((entry) => h === entry);
}

/** Our own shortener, which ed accepts everywhere. Exact host, plus `www.`. */
export function isOwnShortener(href) {
  return hostIn(hostOf(href), OWN_SHORTENER_HOSTS);
}

/**
 * Every structural red flag on a link, each with the sentence the user is shown.
 *
 * STRUCTURAL means judged from the URL's shape alone — no domain allow-list and no reputation
 * lookup, which are #2037 and deliberately not here.
 *
 * @returns {Array<{kind: string, detail: string, message: string}>}
 */
export function linkProblems(href) {
  const raw = String(href == null ? '' : href).trim();
  const out = [];

  if (!isAllowedHref(raw)) {
    const scheme = schemeOf(raw);
    out.push({
      kind: 'link',
      detail: raw,
      message: scheme === 'http'
        ? 'Use an https:// address — http:// links are not allowed.'
        : `Links must be https:// or mailto: — "${raw}" is not.`,
    });

    return out;   // nothing below can be judged on a url we will not accept at all
  }

  // mailto: has no host to judge, and the rules below are all about hosts.
  if (schemeOf(raw) === 'mailto') return out;

  // Our own shortener short-circuits the shortener rule — and only that rule.
  const own = isOwnShortener(raw);
  const host = hostOf(raw);

  if (hasCredentials(raw)) {
    out.push({
      kind: 'link-credentials',
      detail: raw,
      message: 'Remove the username before the @ in the link — a link like '
        + 'https://name@example.com does not go where it appears to.',
    });
  }
  if (isIpHost(host)) {
    out.push({
      kind: 'link-ip',
      detail: host,
      message: `Link to a domain name, not an address — "${host}" is a raw IP address.`,
    });
  }
  if (host.indexOf('xn--') !== -1) {
    out.push({
      kind: 'link-punycode',
      detail: host,
      message: `"${host}" is a punycode host, which can be made to look like a different name. `
        + 'Use the plain domain.',
    });
  }
  if (!own && hostIn(host, THIRD_PARTY_SHORTENERS)) {
    out.push({
      kind: 'link-shortener',
      detail: host,
      message: `Link shorteners are not allowed because the destination cannot be checked — `
        + `use the full address, or shorten it with cs3.me instead of ${host}.`,
    });
  }

  return out;
}

/**
 * What a reader should see as the tooltip: where the link actually goes.
 *
 * ed, 2026-10-04: "show each link's real destination as its title/tooltip (cs3.me expanded via
 * the YOURLS API if resolvable)".
 *
 * ⚠ NO NETWORK HAPPENS HERE, and it cannot. `app/Services/Yourls.php`'s own docblock says
 * `/yourls-api.php` is IP-allowlisted to the VPC + Tailscale, so the browser cannot resolve a
 * cs3.me link — and the display path is the UNAUTHENTICATED login page, where an expand endpoint
 * would be an open URL-resolver and an SSRF surface on the page that must be hardest.
 *
 * So the expansion is resolved SERVER-SIDE at save time and stored in the anchor's `title`
 * (`App\Support\LinkTitles`). This function keeps a stored title when there is one, and
 * otherwise derives the best honest answer from the href alone: the full URL for a normal link,
 * the bare host for one of ours with no expansion stored — ed's stated fallback.
 */
export function linkTitle(href, storedTitle) {
  const raw = String(href == null ? '' : href).trim();
  const stored = String(storedTitle == null ? '' : storedTitle).trim();

  if (stored !== '') return stored;
  if (raw === '') return '';
  if (schemeOf(raw) === 'mailto') return raw;
  if (isOwnShortener(raw)) return hostOf(raw) || raw;

  return raw;
}

/**
 * A `style` value with only allow-listed declarations left, or '' when none survive.
 *
 * ⚠ THIS IS THE ONLY CSS FILTER IN THE STACK. DOMPurify 2.x does not parse CSS, so without this
 * a `style` attribute reaches the login page verbatim.
 *
 * Order matters: the categorical patterns are checked against the WHOLE block first, because
 * `color:red;background:url(javascript:1)` has a perfectly good first declaration.
 */
export function filterStyle(style) {
  const raw = String(style == null ? '' : style);
  if (raw.trim() === '') return '';

  for (const rule of STYLE_FORBIDDEN) {
    if (rule.pattern.test(raw)) return '';
  }

  const kept = [];
  for (const decl of raw.split(';')) {
    const idx = decl.indexOf(':');
    if (idx === -1) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    const check = STYLE_PROPERTIES[prop];
    if (!check || value === '') continue;
    if (check(value)) kept.push(`${prop}: ${value}`);
  }

  return kept.join('; ');
}

/** Every problem in a `style` attribute, for the save-time refusal. */
export function styleProblems(style, tag) {
  const raw = String(style == null ? '' : style);
  const out = [];
  if (raw.trim() === '') return out;

  for (const rule of STYLE_FORBIDDEN) {
    if (rule.pattern.test(raw)) {
      out.push({ kind: 'style', detail: rule.label, message: `${rule.label} is not allowed.` });
    }
  }

  for (const decl of raw.split(';')) {
    const idx = decl.indexOf(':');
    if (idx === -1) {
      if (decl.trim() !== '') {
        out.push({ kind: 'style', detail: decl.trim(), message: `"${decl.trim()}" is not a style setting.` });
      }
      continue;
    }
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    const check = STYLE_PROPERTIES[prop];
    if (!check) {
      out.push({
        kind: 'style',
        detail: prop,
        message: `The ${prop} style is not allowed${tag ? ` on <${tag}>` : ''}. `
          + 'Only colour, highlight, font size and alignment can be set.',
      });
      continue;
    }
    if (!check(value)) {
      out.push({
        kind: 'style',
        detail: `${prop}: ${value}`,
        message: prop === 'font-size'
          ? `"${value}" is not one of the available font sizes (${FONT_SIZES.join(', ')}).`
          : prop === 'font-family'
            ? `"${value}" is not one of the available fonts. Choose one from the font menu.`
            : `"${value}" is not a valid ${prop}.`,
      });
    }
  }

  return out;
}

/**
 * The profile the CURRENT sanitize() call is using.
 *
 * ⚠ This looks like mutable global state because DOMPurify's hook API is global — a hook is
 * registered once per page and cannot be given an argument. It is set immediately before
 * `DOMPurify.sanitize()` and read synchronously inside the hooks that same call, and JavaScript
 * gives no opportunity for another sanitize to interleave between those two statements. Passing
 * the profile down any other way would mean registering and unregistering hooks per call, which
 * is slower and far easier to get wrong.
 */
let activeProfile = PROFILES[DEFAULT_PROFILE];

let hooked = false;

/**
 * Install the two hooks once per page.
 *
 * DOMPurify's own `ALLOWED_URI_REGEXP` could express "https or mailto", but a hook is used
 * instead so the SAME `isAllowedHref` decides it here, in the paste path, and in the editor's
 * link dialog — one function, three callers, no second spelling of the rule.
 */
function installHooks() {
  if (hooked || typeof DOMPurify.addHook !== 'function') return;
  hooked = true;

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    const tag = String(node.nodeName || '').toLowerCase();
    const allowed = activeProfile.attr[tag] || [];

    // Re-impose the PER-TAG map that flattening for DOMPurify threw away: no `style` on an
    // anchor, no `href` on a span, no `title` on a paragraph.
    if (allowed.indexOf(data.attrName) === -1) {
      data.keepAttr = false;
      data.forceKeepAttr = false;

      return;
    }

    if (data.attrName === 'href' && !isAllowedHref(data.attrValue)) {
      // Drop the attribute, keep the text. A link whose target we refuse should still read.
      data.keepAttr = false;
      data.forceKeepAttr = false;

      return;
    }

    if (data.attrName === 'href' && linkProblems(data.attrValue).length) {
      // A structurally red-flagged link (credentials, raw IP, punycode, third-party shortener)
      // is dropped on DISPLAY too, not only refused on save — a row stored before these rules
      // existed must not become a live link to somewhere a reviewer could not judge.
      data.keepAttr = false;
      data.forceKeepAttr = false;

      return;
    }

    if (data.attrName === 'style') {
      // THE ONLY CSS FILTER IN THE STACK — DOMPurify 2.x does not parse CSS.
      const filtered = filterStyle(data.attrValue);
      if (filtered === '') {
        data.keepAttr = false;
        data.forceKeepAttr = false;
      } else {
        data.attrValue = filtered;
      }
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (String(node.nodeName || '').toLowerCase() !== 'a') return;

    const href = node.getAttribute('href');
    if (!href) {
      // href was dropped above: leave inert text rather than a link to nowhere, and do not
      // leave a title claiming a destination the anchor no longer has.
      node.removeAttribute('target');
      node.removeAttribute('rel');
      node.removeAttribute('title');

      return;
    }
    node.setAttribute('target', LINK_ATTRS.target);
    node.setAttribute('rel', LINK_ATTRS.rel);

    // ed, 2026-10-04 — the reader sees where it goes before clicking. A title already stored
    // (a cs3.me expansion resolved server-side at save time) is kept; otherwise it is derived
    // from the href with no network call. See linkTitle().
    const title = linkTitle(href, node.getAttribute('title'));
    if (title !== '') node.setAttribute('title', title);
  });
}

/** DOMPurify config built FROM the active profile, so the two cannot drift. */
function config(prof) {
  return {
    ALLOWED_TAGS: prof.tags.slice(),
    ALLOWED_ATTR: flatAttr(prof.attr),
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    FORBID_CONTENTS: ['script', 'style'],
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    SANITIZE_DOM: true,
  };
}

/**
 * Google Docs wraps an entire paste in `<b style="font-weight:normal" id="docs-internal-guid-…">`.
 *
 * Left alone, the filter keeps the `<b>` and drops the style it depends on — so the WHOLE pasted
 * policy comes out bold. Measured on a real Google Docs fragment while planning #2041, and it is
 * exactly the "pasted formatting must survive cleanly" problem ed named.
 *
 * ⚠ THIS MUST NOT BE A DOMPurify HOOK, and my first attempt was one. Unwrapping the node inside
 * `uponSanitizeElement` re-parents its children to positions DOMPurify's NodeIterator has
 * ALREADY PASSED, so they are never visited: on the Google Docs fragment the reported removals
 * fell from 12 to 2 and `style="line-height:1.38"` / `font-family:Arial` survived untouched. A
 * hook that quietly disables the sanitizer is worse than no hook.
 *
 * So the unwrap happens HERE, on an inert `DOMParser` document, BEFORE DOMPurify sees the markup
 * — and DOMPurify then makes one complete, uninterrupted pass over the result. Parsing as
 * `text/html` executes nothing and loads no subresource.
 *
 * No DOM (a plain-node unit test) means no unwrap, which is safe: `sanitize()` escapes everything
 * on that path anyway.
 */
export function unwrapPasteWrappers(html) {
  const s = String(html == null ? '' : html);
  if (s === '' || typeof DOMParser === 'undefined') return s;
  // Cheap reject so the common case never pays for a parse.
  if (!/font-weight\s*:\s*normal/i.test(s) && s.indexOf('docs-internal-guid') === -1) return s;

  let doc;
  try {
    doc = new DOMParser().parseFromString(s, 'text/html');
  } catch (e) {
    return s;
  }
  if (!doc || !doc.body) return s;

  for (const node of Array.from(doc.body.querySelectorAll('b, strong'))) {
    const style = String(node.getAttribute('style') || '').toLowerCase();
    const id = String(node.getAttribute('id') || '').toLowerCase();
    const isWrapper = /font-weight\s*:\s*normal/.test(style) || id.indexOf('docs-internal-guid') === 0;
    if (!isWrapper || !node.parentNode) continue;

    while (node.firstChild) node.parentNode.insertBefore(node.firstChild, node);
    node.parentNode.removeChild(node);
  }

  return doc.body.innerHTML;
}

/**
 * Safe HTML for `v-html`. NEVER THROWS.
 *
 * Used on display (so a value stored before #2036 cannot run script) and on paste. Where there
 * is no DOM — a unit test in plain node — it falls back to escaping EVERYTHING rather than
 * returning the input, because returning unsanitised markup from a function called `sanitize`
 * is the worst possible failure mode.
 */
export function sanitize(html, profileName) {
  const s = String(html == null ? '' : html);
  if (s === '') return '';

  if (typeof document === 'undefined' || typeof DOMPurify.sanitize !== 'function') {
    return escapeHtml(s);
  }

  installHooks();
  activeProfile = profileOf(profileName);
  try {
    return DOMPurify.sanitize(unwrapPasteWrappers(s), config(activeProfile));
  } catch (e) {
    // A sanitizer that throws must still not pass markup through.
    return escapeHtml(s);
  }
}

/** Last-resort escape, for the no-DOM path. */
export function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Sanitize, and say what went — the pair that makes "stripped on paste with a message" honest.
 *
 * `DOMPurify.removed` is the array of nodes and attributes the last call dropped. It is read
 * immediately after `sanitize`, before any other call can overwrite it.
 *
 * @returns {{html: string, removed: string[]}} `removed` is human-readable, deduplicated,
 *   and ordered as encountered — it goes straight into the message the user sees.
 */
export function sanitizeWithReport(html, profileName) {
  const s = String(html == null ? '' : html);
  if (s === '') return { html: '', removed: [] };

  if (typeof document === 'undefined' || typeof DOMPurify.sanitize !== 'function') {
    return { html: escapeHtml(s), removed: [] };
  }

  installHooks();
  activeProfile = profileOf(profileName);
  let out;
  try {
    out = DOMPurify.sanitize(unwrapPasteWrappers(s), config(activeProfile));
  } catch (e) {
    return { html: escapeHtml(s), removed: [] };
  }

  const removed = [];
  const seen = {};
  const push = (label) => {
    if (!label || seen[label]) return;
    seen[label] = true;
    removed.push(label);
  };

  for (const entry of DOMPurify.removed || []) {
    if (entry && entry.attribute) {
      const on = entry.from && entry.from.nodeName ? entry.from.nodeName.toLowerCase() : 'an element';
      // `style on <body>` and `lang on <body>` are the paste's own wrapper, not the author's
      // content — naming them in the message is noise, the same reason STRUCTURAL exists below.
      if (STRUCTURAL.indexOf(on) !== -1) continue;
      push(`${entry.attribute.name} on <${on}>`);
    } else if (entry && entry.element) {
      const name = entry.element.nodeName ? entry.element.nodeName.toLowerCase() : 'content';
      if (STRUCTURAL.indexOf(name) !== -1) continue;
      push(name === '#text' || name === '#comment' ? 'a comment' : `<${name}>`);
    } else {
      push('unsupported content');
    }
  }

  // A refused href is dropped by our own hook, which DOMPurify does not journal.
  for (const href of hrefsInByRegex(s)) {
    if (!isAllowedHref(href)) {
      const scheme = schemeOf(href);
      push(scheme ? `a ${scheme}: link` : 'a link with no https:// address');
    }
  }

  return { html: out, removed };
}

/**
 * What the markup actually CONTAINS: tags, attributes, hrefs — read from a parsed document.
 *
 * ⚠ This used to regex the raw string, and that was wrong in the direction that matters. A
 * message reading `turnaround < 8 hours` has no `<` element in it, but `/<\s*\/?\s*([a-z]...)/`
 * matches `< 8`'s neighbour and `<p>a < b</p>` yields the "tag" `b`. Rejecting text a human
 * cannot see is exactly the #1952 defect — ed: "the errors aren't highlighted or identified,
 * just error code pops and I cannot save the job."
 *
 * A parse gets it right, and it is the same reading `SafeHtml::validate()` takes with
 * DOMDocument, so the browser's pre-check and the server's verdict agree.
 *
 * The regex path below survives ONLY as a no-DOM fallback for a plain-node unit test. It
 * OVER-reports, which is the safe direction for a check whose job is to refuse.
 *
 * ⚠ KNOWN AND DELIBERATE: the verdict is taken on the document a PARSER sees, so
 * `a < b and c > d` pasted as raw text is read as a `<b>` element carrying `and` and `c`, and is
 * refused. That is what a browser will do with those bytes, so refusing is the correct answer for
 * a security rule — and the WYSIWYG escapes a typed `<` to `&lt;`, which passes cleanly, so a
 * person typing normally never reaches this.
 */
function walk(html) {
  const tags = [];
  const attrs = [];
  const hrefs = [];
  const seenTag = {};

  if (typeof DOMParser === 'undefined') {
    for (const t of tagsInByRegex(html)) tags.push(t);
    for (const a of attrsInByRegex(html)) attrs.push(a);
    for (const h of hrefsInByRegex(html)) hrefs.push(h);

    return { tags, attrs, hrefs, parsed: false };
  }

  // `text/html` never executes anything and loads no subresource — parsing is inert.
  const doc = new DOMParser().parseFromString(String(html == null ? '' : html), 'text/html');

  // ⚠ THE WHOLE DOCUMENT, NOT JUST `body`. This scanned `doc.body` and therefore MISSED a bare
  // `<script>alert(1)</script>`: the HTML parser hoists a leading script into `<head>`, so
  // `body.querySelectorAll('*')` returned nothing and findProblems said "saves". The server
  // refused it anyway (`SafeHtml::validate` walks the whole document, and DOMPurify strips
  // script regardless), so it was never a hole — but it broke the property this file exists to
  // provide: that the editor's pre-check agrees with the server's verdict instead of telling
  // someone their text is fine and then watching the save bounce. Same for `<style>`,
  // `<title>`, `<meta>`, `<link>` and `<base>`.
  const all = doc.querySelectorAll('*');
  for (const el of all) {
    const tag = el.nodeName.toLowerCase();
    // The parser ALWAYS invents html/head/body, so reporting them as disallowed tags would
    // refuse every value. Their ATTRIBUTES are still read: an implied wrapper has none, so
    // anything there (`<body onload=…>`) was written by the author.
    const structural = STRUCTURAL.indexOf(tag) !== -1;
    if (!structural && !seenTag[tag]) { seenTag[tag] = true; tags.push(tag); }
    for (const attr of el.attributes || []) {
      attrs.push({ tag, attr: attr.name.toLowerCase(), value: attr.value });
      if (attr.name.toLowerCase() === 'href') hrefs.push(attr.value);
    }
  }

  return { tags, attrs, hrefs, parsed: true };
}

/** Every `href` in the source, read as text so it works with or without a DOM. */
function hrefsInByRegex(html) {
  const out = [];
  const re = /<a\b[^>]*?\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    out.push(m[2] !== undefined ? m[2] : m[3] !== undefined ? m[3] : m[4] || '');
  }

  return out;
}

/**
 * The message shown when a paste was trimmed. Names the offenders, like #1952's
 * `describeContentProblems` — "Content contains invalid text" is the bug, not the fix.
 */
/** How many offenders to name before switching to a count. */
export const REMOVED_NAME_LIMIT = 3;

/**
 * The message shown when a paste was trimmed.
 *
 * ⚠ THIS USED TO BE UNREADABLE, and #2041 is where it showed. A real Word paste reports 22
 * removals and a Google Docs paste 12, and listing them all produced:
 *
 *   "Removed style on <body>, lang on <body>, <h1>, style on <h1>, style on <span>, style on
 *    <p>, class on <p>, <o:p>, <h2>, a comment, class on <li>, href on <a>, <table>, border on
 *    <table>, <tbody>, <tr>, <td>, <font>, color on <font>, size on <font>, face on <font> and
 *    a http: link — they are not allowed here."
 *
 * Nobody reads that, and ed asked specifically that the message be readable. So:
 *
 *  - a REFUSED LINK is always named first, because it is the only entry a person must act on;
 *  - then up to REMOVED_NAME_LIMIT other offenders;
 *  - then a count, not a list;
 *  - and it always ends by saying the text was kept, because that is the question someone who
 *    just pasted three pages actually has.
 */
export function describeRemoved(removed) {
  const list = (removed || []).slice();
  if (list.length === 0) return '';

  // Links first: the only removal whose loss changes what the page means.
  const links = list.filter((x) => / link$/.test(x));
  const rest = list.filter((x) => !/ link$/.test(x));
  const named = links.concat(rest).slice(0, Math.max(links.length, REMOVED_NAME_LIMIT));
  const others = list.length - named.length;

  let out = 'Removed ' + (named.length === 1
    ? named[0]
    : named.slice(0, -1).join(', ') + ' and ' + named[named.length - 1]);
  if (others > 0) {
    out += `, plus ${others} other formatting ${others === 1 ? 'detail' : 'details'}`;
  }

  return out + '. Your text was kept.';
}

/**
 * What the SERVER will refuse, computed in the browser so the editor can say so first.
 *
 * Deliberately the same shape as #1952's `findContentProblems`: a list of located findings, each
 * naming the thing to remove. Mirrors `SafeHtml::validate()`; the agreement test pins the lists,
 * and `safe-html-rules-2036.mjs` pins the verdicts.
 *
 * @returns {Array<{kind: string, detail: string, message: string}>} empty when it would save
 */
export function findProblems(html, profileName) {
  const s = String(html == null ? '' : html);
  const prof = profileOf(profileName);
  const found = [];

  if (s.length > prof.maxLength) {
    found.push({
      kind: 'length',
      detail: String(s.length),
      message: `The text is ${s.length} characters; the limit is ${prof.maxLength}.`,
    });
  }

  const seen = walk(s);

  for (const tag of seen.tags) {
    if (prof.tags.indexOf(tag) === -1) {
      found.push({ kind: 'tag', detail: tag, message: `<${tag}> is not allowed here.` });
    }
  }

  const reported = {};
  for (const { tag, attr, value } of seen.attrs) {
    const allowed = prof.attr[tag] || [];
    const key = `${attr} on ${tag}`;
    if (allowed.indexOf(attr) === -1) {
      if (!reported[key]) {
        reported[key] = true;
        found.push({
          kind: 'attribute',
          detail: key,
          message: `The ${attr} attribute is not allowed on <${tag}>.`,
        });
      }
      continue;
    }
    // An ALLOWED attribute can still carry a refused value — that is the whole point of
    // letting `style` back in for colour/size/alignment.
    if (attr === 'style') {
      for (const problem of styleProblems(value, tag)) {
        if (reported[problem.detail]) continue;
        reported[problem.detail] = true;
        found.push(problem);
      }
    }
  }

  for (const href of seen.hrefs) {
    for (const problem of linkProblems(href)) {
      if (reported[problem.detail + problem.kind]) continue;
      reported[problem.detail + problem.kind] = true;
      found.push(problem);
    }
  }

  return found;
}

/** True when the value would be refused on save. */
export function hasProblem(html, profileName) {
  return findProblems(html, profileName).length > 0;
}

/** One sentence for the user, field named, every offender listed. ed's #1952 rule. */
export function describeProblems(findings, field = 'Login page message') {
  const list = findings || [];
  if (list.length === 0) return '';

  return `${field}: ` + list.map((f) => f.message).join(' ');
}

/** Fallback only — see walk(). Lower-cased tag names, deduplicated. OVER-reports. */
function tagsInByRegex(html) {
  const out = [];
  const seen = {};
  const re = /<\s*\/?\s*([a-z][a-z0-9-]*)\b/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    const t = m[1].toLowerCase();
    if (!seen[t]) { seen[t] = true; out.push(t); }
  }

  return out;
}

/** Fallback only — see walk(). `{tag, attr}` per attribute written. OVER-reports. */
function attrsInByRegex(html) {
  const out = [];
  const tagRe = /<\s*([a-z][a-z0-9-]*)\b([^>]*)>/gi;
  let t;
  while ((t = tagRe.exec(html)) !== null) {
    const tag = t[1].toLowerCase();
    const attrRe = /([a-z_:][a-z0-9_:.-]*)\s*(?:=\s*("[^"]*"|'[^']*'|[^\s>]+))?/gi;
    let a;
    while ((a = attrRe.exec(t[2])) !== null) {
      const attr = a[1].toLowerCase();
      if (attr === '/') continue;
      const raw = a[2] === undefined ? '' : a[2];
      // Strip the quotes the capture kept, so a caller sees the same value the DOM path gives.
      const value = /^["']/.test(raw) ? raw.slice(1, -1) : raw;
      out.push({ tag, attr, value });
    }
  }

  return out;
}

/**
 * The CKEditor 4 `allowedContent` string, generated from the allow-list so the toolbar cannot
 * offer what the rules refuse.
 *
 * ⚠ CKEditor's Advanced Content Filter spells attributes and styles DIFFERENTLY:
 * `a[href]` allows the href ATTRIBUTE, `p{text-align}` allows the text-align STYLE. Writing
 * `p[style]` would allow the attribute wholesale and ACF would then not police the properties
 * — so a style-bearing tag is emitted as `tag{prop,prop,…}` from STYLE_PROPERTIES, and the
 * `style` entry in ALLOWED_ATTR is deliberately NOT turned into `[style]`.
 *
 * Shape: `p{color,…}; br; b; a[href,target,rel,title]`.
 */
export function ckEditorAllowedContent(profileName) {
  const prof = profileOf(profileName);
  const props = Object.keys(STYLE_PROPERTIES).filter(
    (prop) => prop !== 'font-family' || STYLE_FONT_FAMILY_ENABLED,
  );

  return prof.tags
    .map((tag) => {
      const attrs = (prof.attr[tag] || []).filter((a) => a !== 'style');
      const hasStyle = (prof.attr[tag] || []).indexOf('style') !== -1;
      let out = tag;
      if (attrs.length) out += `[${attrs.join(',')}]`;
      if (hasStyle) out += `{${props.join(',')}}`;

      return out;
    })
    .join('; ');
}

/**
 * CKEditor 4's `font_names`, generated from FONT_FAMILIES so the menu and the validator cannot
 * drift. Format: `Label/stack;Label/stack`, where the label is the stack's first family.
 */
export function ckEditorFontNames() {
  return FONT_FAMILIES
    .map((stack) => `${stack.split(',')[0].trim()}/${stack}`)
    .join(';');
}

/** CKEditor 4's `fontSize_sizes`, generated from FONT_SIZES for the same reason. */
export function ckEditorFontSizes() {
  return FONT_SIZES.map((size) => `${parseInt(size, 10)}/${size}`).join(';');
}

export default {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_SCHEMES,
  LINK_ATTRS,
  MAX_LENGTH,
  STYLE_PROPERTIES,
  STYLE_FORBIDDEN,
  PROFILES,
  POLICY_TAGS,
  POLICY_ATTR,
  POLICY_MAX_LENGTH,
  profileOf,
  unwrapPasteWrappers,
  FONT_SIZES,
  FONT_FAMILIES,
  TEXT_ALIGNS,
  normaliseFontFamily,
  ckEditorFontNames,
  ckEditorFontSizes,
  OWN_SHORTENER_HOSTS,
  THIRD_PARTY_SHORTENERS,
  filterStyle,
  styleProblems,
  linkProblems,
  linkTitle,
  hostOf,
  hasCredentials,
  isIpHost,
  isOwnShortener,
  sanitize,
  sanitizeWithReport,
  describeRemoved,
  findProblems,
  hasProblem,
  describeProblems,
  isAllowedHref,
  schemeOf,
  escapeHtml,
  ckEditorAllowedContent,
};
